import type { Profile } from '@/types';

/** Where a section is drawn in the backoffice sidebar. Presentation only, like `label`. */
export type BackofficeGroupId =
  | 'overview'
  | 'catalog'
  | 'pricing'
  | 'contracts'
  | 'logistics'
  | 'finance';

/** Sub-navigation of the backoffice. Labels are product copy and therefore pt-BR. */
export interface BackofficeSection {
  readonly to: string;
  readonly label: string;
  readonly group: BackofficeGroupId;
  /** Profiles admitted to the API route this section opens. See the block comment below. */
  readonly profiles: readonly Profile[];
}

/**
 * The sidebar's groups: first the panel the day starts from (the overview and the indicators),
 * then the order a record travels — a product is registered, priced, sold under a contract,
 * delivered, and finally reconciled.
 */
export const BACKOFFICE_GROUPS: readonly {
  readonly id: BackofficeGroupId;
  readonly label: string;
}[] = [
  { id: 'overview', label: 'Painel' },
  { id: 'catalog', label: 'Cadastro' },
  { id: 'pricing', label: 'Preços' },
  { id: 'contracts', label: 'Contratos' },
  { id: 'logistics', label: 'Logística' },
  { id: 'finance', label: 'Financeiro' },
];

/**
 * `profiles` on every entry below is a hand-mirror of one row of `ROUTE_PROFILES` in
 * `apps/api/app/api/authorization.py` — the *source of truth* for who may reach what — and of
 * its generated rendering, `docs/architecture/authorization.md`. The API enforces the real
 * gate; this list only decides which tab is worth showing, so a profile is never invited to
 * click into a screen that answers every request with a 403.
 *
 * This is a second copy of information declared once elsewhere because the two live in
 * different runtimes with no shared import between them (Python and TypeScript, in separate
 * deployables) — the alternative is generating this file the way `schema.d.ts` is generated
 * from the OpenAPI document, which is out of scope here. Whoever adds a `RouteGroup` row, or
 * moves a profile into or out of one that a section below already cites, must update the
 * matching `profiles` entry in the same change, or this list quietly drifts from the API again
 * (the exact defect this file exists to fix, e.g. Sprint 4-13's UI defect sweep, item 3).
 */
export const BACKOFFICE_SECTIONS = [
  // Reads the KPI panel, the reconciliation report, receivables, contract positions, price tables
  // and the delivery queue together; only platform_admin holds every one of those rows.
  {
    to: '/backoffice/visao-geral',
    label: 'Visão geral',
    group: 'overview',
    profiles: ['platform_admin'],
  },
  // BACKOFFICE_CUSTOMERS_READ: platform_admin only, for now — whether the commercial backoffice
  // should also register customers is the product owner's call, not a default. First of the
  // group because a customer is where an onboarding starts: everything below is per customer.
  {
    to: '/backoffice/clientes',
    label: 'Clientes',
    group: 'catalog',
    profiles: ['platform_admin'],
  },
  // BACKOFFICE_INACTIVITY_READ: commercial_backoffice, platform_admin. Beside the customers, as the
  // proposal draws it: who is about to stop buying is a fact about the customer base.
  {
    to: '/backoffice/inatividade',
    label: 'Inatividade',
    group: 'catalog',
    profiles: ['commercial_backoffice', 'platform_admin'],
  },
  // BACKOFFICE_REFERENCE_READ: procurement, commercial_backoffice, controllership, platform_admin.
  {
    to: '/backoffice/produtos',
    label: 'Produtos',
    group: 'catalog',
    profiles: ['procurement', 'commercial_backoffice', 'controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/fornecedores',
    label: 'Fornecedores',
    group: 'catalog',
    profiles: ['procurement', 'commercial_backoffice', 'controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/unidades',
    label: 'Unidades de medida',
    group: 'catalog',
    profiles: ['procurement', 'commercial_backoffice', 'controllership', 'platform_admin'],
  },
  // BACKOFFICE_CURATION_READ: commercial_backoffice, controllership, platform_admin. Procurement
  // registers the reference set but does not decide which customer sees what.
  {
    to: '/backoffice/catalogos',
    label: 'Catálogo por cliente',
    group: 'catalog',
    profiles: ['commercial_backoffice', 'controllership', 'platform_admin'],
  },
  // Sprint 4, in the order the pricing chain runs: a cost is what the spread is applied to, and a
  // price table is what the two produce. An operator working left to right never reaches a screen
  // whose inputs do not exist yet.
  // BACKOFFICE_COST_READ: procurement, controllership, platform_admin. Commercial backoffice
  // publishes prices without ever needing to see what Instoque paid (RNF01's whole premise).
  {
    to: '/backoffice/custos',
    label: 'Custo de aquisição',
    group: 'pricing',
    profiles: ['procurement', 'controllership', 'platform_admin'],
  },
  // BACKOFFICE_SPREAD_READ: commercial_backoffice, controllership, platform_admin.
  {
    to: '/backoffice/spreads',
    label: 'Regras de spread',
    group: 'pricing',
    profiles: ['commercial_backoffice', 'controllership', 'platform_admin'],
  },
  // BACKOFFICE_PRICE_TABLE_READ: commercial_backoffice, controllership, platform_admin.
  {
    to: '/backoffice/tabelas-preco',
    label: 'Tabelas de preço',
    group: 'pricing',
    profiles: ['commercial_backoffice', 'controllership', 'platform_admin'],
  },
  // Sprint 6, last on purpose: reconciliation looks at charges that already exist, so it is the
  // one section that has nothing to offer until every earlier one has produced some orders.
  // BACKOFFICE_RECONCILIATION: controllership, platform_admin only — reconciliation is an audit
  // function, not the function of whoever decided what to charge.
  {
    to: '/backoffice/conciliacao',
    label: 'Conciliação',
    group: 'finance',
    profiles: ['controllership', 'platform_admin'],
  },
  // Sprint 7: prazos before entregas, the same "inputs before outputs" ordering as cost → spread
  // → price tables above — the queue's own estimate column reads from the rules, not the other
  // way round, so an operator working left to right can always resolve where a number came from.
  // BACKOFFICE_DELIVERY_CONFIG_READ: commercial_backoffice, controllership, platform_admin.
  // Figma CT3/CT7/CT8: the approvals, the daily settlement and the export as sections of their
  // own. Approving reads price tables; the other two are BACKOFFICE_RECONCILIATION's.
  {
    to: '/backoffice/aprovacoes',
    label: 'Aprovações',
    group: 'pricing',
    profiles: ['controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/fechamento-diario',
    label: 'Fechamento diário',
    group: 'finance',
    profiles: ['controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/exportacao',
    label: 'Exportação',
    group: 'finance',
    profiles: ['controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/prazos',
    label: 'Prazos de entrega',
    group: 'logistics',
    profiles: ['commercial_backoffice', 'controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/fretes-cupons',
    label: 'Fretes e cupons',
    group: 'logistics',
    profiles: ['commercial_backoffice', 'controllership', 'platform_admin'],
  },
  // BACKOFFICE_DELIVERIES: commercial_backoffice, platform_admin only — advancing a delivery is
  // an operational action, not an audit one, so controllership is absent here on purpose.
  // BACKOFFICE_ORDERS: commercial_backoffice, platform_admin — every customer's orders and the
  // status advance (Figma BC3/BC4). The route existed only in the API before the redesign.
  {
    to: '/backoffice/pedidos',
    label: 'Pedidos',
    group: 'logistics',
    profiles: ['commercial_backoffice', 'platform_admin'],
  },
  {
    to: '/backoffice/entregas',
    label: 'Entregas',
    group: 'logistics',
    profiles: ['commercial_backoffice', 'platform_admin'],
  },
  // Sprint 8, in the order the take-or-pay chain runs: a contract is what a position is measured
  // against, and a receivable only exists once a term checkout has happened against one — the
  // same "inputs before outputs" ordering every earlier sprint's own sections already follow.
  // BACKOFFICE_CONTRACTS: commercial_backoffice, platform_admin only — registering or advancing
  // a contract is commercial backoffice's operational function, not controllership's audit one.
  {
    to: '/backoffice/contratos',
    label: 'Contratos',
    group: 'contracts',
    profiles: ['commercial_backoffice', 'platform_admin'],
  },
  {
    to: '/backoffice/posicao-contratual',
    label: 'Posição contratual',
    group: 'contracts',
    profiles: ['commercial_backoffice', 'platform_admin'],
  },
  // BACKOFFICE_RECEIVABLES: controllership, platform_admin only — settling a receivable is
  // closer to money than curating a contract, so it carries controllership's row instead.
  {
    to: '/backoffice/titulos',
    label: 'Títulos a receber',
    group: 'contracts',
    profiles: ['controllership', 'platform_admin'],
  },
  // Sprint 12, last of all: an indicator is read off everything the sections above already
  // produce, so it is the one screen with nothing to show until every earlier one has.
  // BACKOFFICE_KPIS: controllership, platform_admin only (SPRINT12.md names the row directly).
  // BACKOFFICE_PAYMENTS: commercial_backoffice, controllership, platform_admin — read-only, every
  // customer's charges (Figma BC15/CT11).
  {
    to: '/backoffice/pagamentos',
    label: 'Pagamentos dos clientes',
    group: 'finance',
    profiles: ['commercial_backoffice', 'controllership', 'platform_admin'],
  },
  {
    to: '/backoffice/indicadores',
    label: 'Indicadores',
    group: 'overview',
    profiles: ['controllership', 'platform_admin'],
  },
] as const satisfies readonly BackofficeSection[];

/**
 * Sections the given profile's tab should show. `null` (no profile claim) sees none, matching
 * every other profile-gated list in the app (`navigationForProfile` does the same).
 */
export function backofficeSectionsForProfile(
  profile: Profile | null,
): readonly BackofficeSection[] {
  if (profile === null) {
    return [];
  }
  return BACKOFFICE_SECTIONS.filter((section) =>
    (section.profiles as readonly Profile[]).includes(profile),
  );
}

/**
 * BACKOFFICE_REFERENCE_WRITE: procurement, platform_admin — narrower than the read row above,
 * because registering or editing a SKU is the procurement function specifically (it is the
 * profile that talks to suppliers and knows how a product is packed), not the commercial
 * backoffice's or controllership's. The SKU create/edit/import screens are not tabs of their
 * own (they are reached from "Produtos"), so they are not in `BACKOFFICE_SECTIONS`, but they
 * still need this same check before rendering an interactive form — a profile that cannot
 * write must not be able to fill in the whole thing before a 403 tells it so (Sprint 4-13's
 * UI defect sweep, item 3, second half).
 */
export const BACKOFFICE_REFERENCE_WRITE_PROFILES: readonly Profile[] = [
  'procurement',
  'platform_admin',
];

export interface BackofficeGroup {
  readonly id: BackofficeGroupId;
  readonly label: string;
  readonly sections: readonly BackofficeSection[];
}

/** The sections a profile sees, grouped for the sidebar; a group with none of them is left out. */
export function backofficeGroupsForProfile(profile: Profile | null): readonly BackofficeGroup[] {
  const sections = backofficeSectionsForProfile(profile);
  return BACKOFFICE_GROUPS.map((group) => ({
    ...group,
    sections: sections.filter((section) => section.group === group.id),
  })).filter((group) => group.sections.length > 0);
}

/**
 * Where `/backoffice` lands, per profile: the screen each one opens first on most days —
 * the overview for the administrator, the indicators for controllership, acquisition cost for
 * procurement — rather than the product registry for everybody. Always one of the profile's own
 * sections.
 */
const HOME_BY_PROFILE: Partial<Record<Profile, string>> = {
  // Kept on the product registry where it always opened; Inatividade, now first of its sections,
  // is one click away rather than the new landing nobody asked for.
  commercial_backoffice: '/backoffice/produtos',
  controllership: '/backoffice/indicadores',
  platform_admin: '/backoffice/visao-geral',
  procurement: '/backoffice/custos',
};

export function backofficeHomeForProfile(profile: Profile | null): string | undefined {
  const sections = backofficeSectionsForProfile(profile);
  const preferred = profile === null ? undefined : HOME_BY_PROFILE[profile];
  return sections.find((section) => section.to === preferred)?.to ?? sections[0]?.to;
}

/** One group of "Todas as áreas" (Figma 94:1972): a label and the sections under it, in order. */
export interface BackofficeArea {
  readonly label: string;
  readonly sections: readonly BackofficeSection[];
}

/**
 * How the redesign groups a profile's sections under "Todas as áreas" and in the footer (Figma
 * "Esboço · perfis internos", note 94:1972). Presentation only: the sections themselves — and
 * so who may open what — still come from `BACKOFFICE_SECTIONS`; a path the profile cannot reach
 * is simply not listed, and a section a profile can reach that no area names lands in "Outras".
 */
const AREAS_BY_PROFILE: Partial<
  Record<Profile, readonly (readonly [string, readonly string[]])[]>
> = {
  commercial_backoffice: [
    ['Clientes e catálogo', ['catalogos', 'inatividade']],
    ['Cadastro', ['produtos', 'fornecedores', 'unidades']],
    ['Preços', ['spreads', 'tabelas-preco']],
    ['Pedidos e logística', ['pedidos', 'entregas', 'prazos', 'fretes-cupons']],
    ['Contratos e cobrança', ['contratos', 'posicao-contratual', 'pagamentos']],
  ],
  procurement: [
    ['Cadastro', ['produtos', 'fornecedores', 'unidades']],
    ['Preços', ['custos']],
  ],
  controllership: [
    ['Painel', ['indicadores']],
    ['Preços', ['aprovacoes', 'tabelas-preco', 'spreads', 'catalogos', 'custos']],
    ['Financeiro', ['fechamento-diario', 'conciliacao', 'titulos', 'pagamentos', 'exportacao']],
    ['Consulta', ['produtos', 'fornecedores', 'unidades', 'prazos', 'fretes-cupons']],
  ],
  platform_admin: [
    ['Painel', ['visao-geral', 'indicadores']],
    ['Clientes', ['clientes', 'inatividade', 'catalogos']],
    ['Cadastro', ['produtos', 'fornecedores', 'unidades']],
    ['Preços', ['custos', 'spreads', 'tabelas-preco', 'aprovacoes']],
    ['Pedidos e logística', ['pedidos', 'entregas', 'prazos', 'fretes-cupons']],
    [
      'Contratos e financeiro',
      ['contratos', 'posicao-contratual', 'titulos', 'conciliacao', 'pagamentos'],
    ],
  ],
};

export function backofficeAreasForProfile(profile: Profile | null): readonly BackofficeArea[] {
  const sections = backofficeSectionsForProfile(profile);
  const plan = profile === null ? undefined : AREAS_BY_PROFILE[profile];
  if (plan === undefined) {
    return backofficeGroupsForProfile(profile).map((group) => ({
      label: group.label,
      sections: group.sections,
    }));
  }
  const byPath = new Map(
    sections.map((section) => [section.to.replace('/backoffice/', ''), section]),
  );
  const used = new Set<string>();
  const areas = plan
    .map(([label, paths]) => ({
      label,
      sections: paths
        .map((path) => {
          const section = byPath.get(path);
          if (section !== undefined) used.add(section.to);
          return section;
        })
        .filter((section): section is BackofficeSection => section !== undefined),
    }))
    .filter((area) => area.sections.length > 0);
  const rest = sections.filter((section) => !used.has(section.to));
  return rest.length > 0 ? [...areas, { label: 'Outras', sections: rest }] : areas;
}

/** The second line of the internal header (Figma BC1, CP1, CT1, AD1): each profile's daily stops. */
const SHORTCUTS_BY_PROFILE: Partial<Record<Profile, readonly string[]>> = {
  commercial_backoffice: ['pedidos', 'tabelas-preco', 'catalogos', 'entregas', 'inatividade'],
  procurement: ['produtos', 'custos', 'fornecedores', 'unidades'],
  controllership: ['indicadores', 'aprovacoes', 'conciliacao', 'fechamento-diario', 'titulos'],
  platform_admin: ['clientes', 'inatividade', 'pedidos', 'indicadores'],
};

export function backofficeShortcutsForProfile(
  profile: Profile | null,
): readonly BackofficeSection[] {
  const sections = backofficeSectionsForProfile(profile);
  const wanted = (profile === null ? undefined : SHORTCUTS_BY_PROFILE[profile]) ?? [];
  return wanted
    .map((path) => sections.find((section) => section.to === `/backoffice/${path}`))
    .filter((section): section is BackofficeSection => section !== undefined);
}
